import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { BorderRadiusProps, BoxShadowProps, WidthProps } from 'styled-system';
import {
  alignItems,
  borderBottom,
  borderColor,
  borderRadius,
  color,
  display,
  space,
  width,
} from 'styled-system';

import { useLocalization } from '../../providers/LocaleProvider';
import { Button } from '../Button';
import Icon from '../Icon';
import { HeadlineH7 } from '../Typography';

interface DrawerHeaderProps extends BoxShadowProps, PropsWithChildren {
  headline: string;
  withCloseIcon?: boolean;
  onCloseClick?: () => void;
  id?: string;
  rightAlignClose?: boolean;
}

const ICON_SIZE = 32;

const Header = styled.div`
  ${display};
  ${alignItems};
  ${space};
  ${borderBottom};
  ${borderColor};
  ${color};
`;
const headerStyle = {
  'data-testid': 'drawer-header',
  display: 'flex',
  alignItems: 'center',
  p: 'sp_16',
  borderBottom: '1px solid',
  borderColor: 'sys.neutral.border.weak',
  backgroundColor: 'sys.neutral.background.default',
};

const Headline = styled(HeadlineH7)<WidthProps>`
  ${width};
`;

const headLineStyle: ComponentProps<typeof Headline> = {
  as: 'h1',
  textAlign: 'center',
  width: 1,
  margin: 'sp_0',
  color: 'sys.neutral.text.strong',
};

const StyledButton = styled(Button)<BorderRadiusProps>`
  ${borderRadius};
`;

const CloseButton = ({
  withCloseIcon,
  onCloseClick,
  boxShadow,
}: Pick<DrawerHeaderProps, 'withCloseIcon' | 'onCloseClick' | 'boxShadow'>) => {
  const { t } = useLocalization();

  return withCloseIcon ? (
    <StyledButton
      kind="bare"
      onClick={onCloseClick}
      aria-label={t('h24_close')}
      boxShadow={boxShadow}
      borderRadius="circle"
    >
      <Icon name="close" size={ICON_SIZE} />
    </StyledButton>
  ) : null;
};

const CloseButtonLeftAligned = ({ headline, children, id, ...props }: DrawerHeaderProps) => {
  return (
    <>
      <CloseButton {...props} />
      <Headline data-testid="headline" {...(id ? { id } : {})} {...headLineStyle}>
        {headline}
      </Headline>
      {children}
    </>
  );
};

const CloseButtonRightAligned = ({ headline, children, id, ...props }: DrawerHeaderProps) => {
  return (
    <>
      {children}
      <Headline data-testid="headline" {...(id ? { id } : {})} {...headLineStyle}>
        {headline}
      </Headline>
      <CloseButton {...props} />
    </>
  );
};

const DrawerHeader = ({
  rightAlignClose = false,
  withCloseIcon = true,
  ...props
}: DrawerHeaderProps) => (
  <Header {...headerStyle}>
    {rightAlignClose ? (
      <CloseButtonRightAligned {...props} withCloseIcon={withCloseIcon} />
    ) : (
      <CloseButtonLeftAligned {...props} withCloseIcon={withCloseIcon} />
    )}
  </Header>
);

export default DrawerHeader;
